import { inject, ref } from '@vue/composition-api';
import { useActions } from 'vuex-composition-helpers/dist';
import { kebabCase, sortBy } from 'lodash';

export default (props, { root, emit }) => {
  const config = inject('config');
  const productName = inject('productName');

  // action
  const { showToast } = useActions(['showToast']);

  // ref
  const isLoading = ref(false);
  const subformObserverRef = ref(null);

  const onClaimsAndConvictionValidation = async ({
    step,
    quoteData,
  }) => {
    let data = null;

    try {
      data = await root.$api.claimsAndConvictionValidation({
        productName,
        productPartnerId: config.partner,
        step,
        quoteData,
      });
    } catch (error) {
      console.error(error.toString());

      if (error?.response?.status === 422) {
        await setClaimAndConvictionErrors({
          errors: error.response.data.errors,
          type: step,
        });
      }

      showToast({
        type: 'error',
        text: `${step} validation error`,
      });
    }

    return data;
  };

  const setClaimAndConvictionErrors = async ({
    errors = {},
    type = 'claim',
  }) => {
    const result = Object.fromEntries(
      // convert to array then map and fromEntries back to object
      Object.entries(errors).map(([key, value]) => {
        const observerFieldName = `drivers_${type}_${kebabCase(key)}`;

        return [observerFieldName, value];
      }),
    );
    subformObserverRef.value.setErrors(result);

    scrollToInvalidField({
      observerFields: Object.keys(subformObserverRef.value.fields),
      errorFields: Object.keys(result),
    });

    return result;
  };

  const saveSubform = async ({ observer, subformType, quoteData, questionIds, sectionItemName }) => {
    const isValid = await observer.validate();

    if (!isValid) {
      onFrontendValidationFailed(observer, questionIds, sectionItemName);

      return;
    }

    if (productName === 'car') {
      updateIsLoadingStatus(true);

      const response = await onClaimsAndConvictionValidation({
        step: subformType,
        quoteData,
      });

      if (response?.status === 'success') {
        emit('save', quoteData);
      }

      updateIsLoadingStatus(false);

      return;
    }

    emit('save', quoteData);
  };

  const scrollToInvalidField = ({ observerFields, errorFields }) => {
    let failedField = null;

    for (const observerField of observerFields) {
      if (errorFields.indexOf(observerField) !== -1) {
        failedField = observerField;
        break;
      }
    }

    if (failedField) root.$scrollTo(`#${failedField}`, 300);
  };

  const onFrontendValidationFailed = (observer, questionIds, sectionItemName) => {
    const rowId = questionIds?.length
      ? questionIds.find(qId => observer.fields[`drivers.${sectionItemName}_${qId}`].invalid)
      : sortBy(Object.values(observer.fields), 'id').find(field => field.invalid)?.id;

    if (rowId) root.$scrollTo(`#${rowId}`, 300);
  };

  const updateIsLoadingStatus = (status) => {
    isLoading.value = status;
  };

  const currentFieldError = (observer, fieldName) => {
    return observer.fields?.[fieldName]?.invalid;
  };

  return {
    // refs
    isLoading,
    subformObserverRef,

    // methods
    setClaimAndConvictionErrors,
    saveSubform,
    onClaimsAndConvictionValidation,
    currentFieldError,
  };
};
